/*------------------------------------------------------------------------------
   About      : NBI Insights list table (epic DA-697, II-FE-2)

                Cloned from `config-registry/components/ConfigRegistryTable.tsx`,
                with one deliberate divergence: **paging and sorting are client
                mode, not server mode.** `getInsights` has no page/pageSize/sort
                params, so the grid owns paging over the locally filtered rows.
                Everything else — the DataGridPro styling, pinned actions
                column, density and column-visibility handling — follows the
                config-registry table so the two screens feel the same.

                Every label resolves through the catalog's `displayName`; no raw
                enum name reaches the DOM.

                QA status and Active are **editable inline**. Both are ordinary
                content edits routed through the same `updateAll` path the edit
                screen uses, so both obey the same `TODO`-only rule and are
                disabled with an explanation when the record has moved past it.

   Copyright (c) 2026 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/
import { useMemo, useState } from 'react'
import { Box, Chip, CircularProgress, Switch, Tooltip, Typography,
         useTheme } from '@mui/material'
import { DataGridPro,
         GridActionsCellItem,
         GridColDef,
         GridColumnVisibilityModel,
         GridDensity,
         GridPaginationModel,
         GridRenderCellParams,
         GridRowId,
         GridRowSelectionModel } from '@mui/x-data-grid-pro'
import VisibilityOutlinedIcon    from '@mui/icons-material/VisibilityOutlined'
import PublishIcon               from '@mui/icons-material/Publish'

import { CONTENT_EDIT_STATUS,
         CONTENT_VERIFICATION_STATUS } from 'types/cms/frontendTypes'

import { InsightCatalog, InsightRecord } from '../types/insightTypes'
import { applianceNameFor, displayNameFor } from '../utils/insightCatalogHelpers'
import { deriveInsightValidation }  from '../utils/insightValidation'
import { DIRECTIONS,
         MEASUREMENT_TYPES,
         SENTIMENTS,
         TIME_OF_USE,
         VALIDITY_TYPES,
         insightCategoryLabel,
         labelFor }                 from '../constants/insightClassification'
import { InsightEditStatusBadge,
         InsightValidationDots }    from './InsightStatusBadge'
import { TemplateTokenText }        from './TemplateTokenText'
import { QaStatusSelect }           from './QaStatusSelect'

interface InsightsTableProps {
  rows            : InsightRecord[]
  catalog        ?: InsightCatalog
  isLoading       : boolean
  page            : number
  pageSize        : number
  selectedIds     : string[]
  /** Stored status → display text, from getReviewWorkflow. */
  statusLabels    : Partial<Record<CONTENT_VERIFICATION_STATUS, string>>
  /** documentIds with a write in flight — their inline controls lock. */
  busyIds        ?: string[]
  onPageChange    : (page: number, pageSize: number) => void
  onSelectionChange: (ids: string[]) => void
  onEdit          : (insight: InsightRecord) => void
  /** Publish a single row. Omit to hide the action entirely. */
  onPublish       ?: (insight: InsightRecord) => void
  /**
   * Whether this record can be published right now.
   *
   * Owned by the caller because the answer comes from the workflow ACTION table
   * (`actions[verificationStatus]` containing PUBLISH), not from an inline
   * `=== READY_TO_PUBLISH` comparison — the permitted actions are role-scoped,
   * so a reviewer sitting on a READY_TO_PUBLISH record still cannot publish it.
   */
  canPublish      ?: (insight: InsightRecord) => boolean
  /** Inline QA transition. Omit to render the status read-only. */
  onChangeQaStatus?: (insight: InsightRecord, next: CONTENT_VERIFICATION_STATUS) => void
  /** Inline active/inactive. Receives the NEW `disabled` value. */
  onToggleDisabled?: (insight: InsightRecord, disabled: boolean) => void
}

export const InsightsTable = ({
  rows,
  catalog,
  isLoading,
  page,
  pageSize,
  selectedIds,
  statusLabels,
  busyIds,
  onPageChange,
  onSelectionChange,
  onEdit,
  onPublish,
  canPublish,
  onChangeQaStatus,
  onToggleDisabled,
}: InsightsTableProps) => {

  const theme = useTheme()

  const busy = useMemo(() => new Set(busyIds ?? []), [busyIds])

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
    // Shown by default — the classification an author scans by. Appliance is
    // the useful axis here.
    measurementType : true,
    type            : true,
    applianceId     : true,
    // Available via the column menu; useful when auditing a family, noise otherwise
    // `code` shares the Insight ID cell's job but is the pilot-scoped derived
    // string, not the stable id — off by default, still searchable and still
    // reachable from the column menu when someone needs to quote it.
    code            : false,
    category        : false,
    validityType    : false,
    timeOfUse       : false,
    direction       : false,
    sentimentType   : false,
    group           : false,
    updatedAt       : false,
  })

  const [density, setDensity] = useState<GridDensity>('standard')

  /* Column order is the scan order an author reads in: which insight (ID),
     what kind (Type), which fuel — then appliance, content and state. */
  const columns = useMemo<GridColDef<InsightRecord>[]>(() => [
    {
      field       : 'insightId',
      headerName  : 'Insight ID',
      width       : 140,
      // The id alone. `code` used to ride along underneath in a caption, which
      // made the first column two lines tall for a value most authors never
      // quote — it is still available as its own column from the column menu.
      renderCell  : (params: GridRenderCellParams<InsightRecord>) => (
        <Typography
          variant="body2"
          sx={{ fontFamily : 'monospace', fontWeight : 600, color : theme.palette.primary.main }}
          noWrap
        >
          {params.row.insightId}
        </Typography>
      ),
    },
    {
      field       : 'code',
      headerName  : 'Code',
      width       : 120,
      // Off by default. Unique PER PILOT, not globally — never treat it as a key.
      renderCell  : (params: GridRenderCellParams<InsightRecord>) => (
        <Typography variant="caption" sx={{ fontFamily : 'monospace', color : 'text.secondary' }} noWrap>
          {params.row.code}
        </Typography>
      ),
    },
    {
      field       : 'type',
      headerName  : 'Type',
      flex        : 1,
      minWidth    : 150,
      valueGetter : (_value, row) => displayNameFor(catalog, 'type', row.type),
      renderCell  : (params: GridRenderCellParams<InsightRecord>) => (
        <Tooltip title={params.row.type || ''} placement="top" arrow>
          <Box sx={{ overflow : 'hidden', textOverflow : 'ellipsis', whiteSpace : 'nowrap' }}>
            {params.value}
          </Box>
        </Tooltip>
      ),
    },
    {
      field       : 'measurementType',
      headerName  : 'Fuel',
      width       : 100,
      // Labelled, not raw — these columns printed BILLING_CYCLE / OFF_PEAK /
      // HIGHEST / NEGATIVE straight into the DOM while the vocabularies were
      // private to the form and unreachable from here.
      valueGetter : (_value, row) => labelFor(MEASUREMENT_TYPES, row.measurementType),
      renderCell  : (params: GridRenderCellParams<InsightRecord>) => (
        params.value ? <Chip label={params.value} size="small" variant="outlined" /> : null
      ),
    },
    {
      field       : 'applianceId',
      headerName  : 'Appliance',
      width       : 130,
      valueGetter : (_value, row) => applianceNameFor(catalog, row.applianceId),
    },
    {
      field       : 'templateString',
      headerName  : 'Content preview',
      flex        : 2,
      minWidth    : 240,
      sortable    : false,
      renderCell  : (params: GridRenderCellParams<InsightRecord>) => (
        <TemplateTokenText text={params.row.templateString} catalog={catalog} truncate />
      ),
    },
    {
      field       : 'verificationStatus',
      headerName  : 'QA status',
      width       : 165,
      // Sorting and the quick filter read the DISPLAYED text, and display text
      // comes from the workflow table, never a hardcoded map: READY_FOR_QA
      // shows as "Ready for review" while the stored value stands, because
      // CONTENT_VERIFICATION_STATUS is shared with recos and surveys.
      valueGetter : (_value, row) =>
        statusLabels[row.verificationStatus] ?? row.verificationStatus,
      renderCell  : (params: GridRenderCellParams<InsightRecord>) => {
        const row = params.row

        if (!onChangeQaStatus) {
          return <Chip label={params.value} size="small" variant="outlined" />
        }

        return (
          <QaStatusSelect
            current={row.verificationStatus}
            /* Per-record and computed by quill — the cross-entity gate against
               tagged recommendations means the allowed set cannot be derived
               from the enum. A blocked edge carries its own reason. */
            transitions={row.allowedTransitions ?? []}
            statusLabels={statusLabels}
            disabled={busy.has(row.documentId)}
            minWidth={150}
            onChange={(next) => onChangeQaStatus(row, next)}
          />
        )
      },
    },
    {
      field       : 'disabled',
      headerName  : 'Active',
      width       : 90,
      // `disabled` is the stored field; Active is its inverse. Absent means
      // active, matching quill's `default: false`.
      valueGetter : (_value, row) => row.disabled !== true,
      renderCell  : (params: GridRenderCellParams<InsightRecord>) => {
        const row      = params.row
        const isActive = row.disabled !== true
        const isBusy   = busy.has(row.documentId)

        // Toggling active is a content edit, so it obeys the same TODO-only
        // rule as any other edit. Saying so up front beats a server rejection.
        const lockedByStatus = row.verificationStatus !== CONTENT_VERIFICATION_STATUS.TODO
        const tooltip = lockedByStatus
          ? 'Only editable while QA status is To do'
          : isActive ? 'Active — click to deactivate' : 'Inactive — click to activate'

        if (!onToggleDisabled) {
          return <Chip label={isActive ? 'Active' : 'Inactive'} size="small"
                       color={isActive ? 'success' : 'default'} variant="outlined" />
        }

        return (
          <Tooltip title={tooltip} placement="top" arrow>
            {/* span so the tooltip still fires when the switch is disabled */}
            <span style={{ display : 'inline-flex', alignItems : 'center' }}>
              {isBusy
                ? <CircularProgress size={16} sx={{ mx : 1.5 }} />
                : (
                  <Switch
                    size="small"
                    checked={isActive}
                    disabled={lockedByStatus}
                    onChange={(event) => onToggleDisabled(row, !event.target.checked)}
                    inputProps={{ 'aria-label' : `Active: ${row.insightId}` }}
                  />
                )}
            </span>
          </Tooltip>
        )
      },
    },
    {
      field       : 'editStatus',
      headerName  : 'Status',
      width       : 110,
      renderCell  : (params: GridRenderCellParams<InsightRecord>) => (
        <InsightEditStatusBadge status={params.row.editStatus ?? CONTENT_EDIT_STATUS.DRAFT} />
      ),
    },
    {
      field       : 'validationState',
      headerName  : 'Valid.',
      width       : 80,
      sortable    : false,
      /* Derived from the record and the catalog, not read off `validationState`
         — that field is free-form JSON typed as
         `Record<string, {status, checkedAt, detail?}>` which nothing currently
         writes. Reading three booleans off it made every row show "pending"
         even when the content was plainly fine. */
      renderCell  : (params: GridRenderCellParams<InsightRecord>) => (
        <InsightValidationDots summary={deriveInsightValidation(params.row, catalog)} />
      ),
    },
    {
      field       : 'category',
      headerName  : 'Insight category',
      width       : 140,
      /* Off by default, switchable from the column menu. Reads the record's own
         `category` string — NOT `appliances[].category` from the catalog, which
         is a different field and is what this column originally showed. */
      // Label, not the stored value — the same "never render a raw enum name"
      // rule the catalog-backed columns follow.
      valueGetter : (_value, row) => insightCategoryLabel(row.category),
      renderCell  : (params: GridRenderCellParams<InsightRecord>) => (
        params.value ? <Chip label={params.value} size="small" variant="outlined" /> : null
      ),
    },
    {
      field       : 'validityType',
      headerName  : 'Validity',
      width       : 120,
      valueGetter : (_value, row) => labelFor(VALIDITY_TYPES, row.validityType),
    },
    {
      field       : 'timeOfUse',
      headerName  : 'Time of use',
      width       : 115,
      valueGetter : (_value, row) => labelFor(TIME_OF_USE, row.timeOfUse),
    },
    {
      field       : 'direction',
      headerName  : 'Direction',
      width       : 105,
      valueGetter : (_value, row) => labelFor(DIRECTIONS, row.direction),
    },
    {
      field       : 'sentimentType',
      headerName  : 'Sentiment',
      width       : 110,
      valueGetter : (_value, row) => labelFor(SENTIMENTS, row.sentimentType),
    },
    {
      field       : 'group',
      headerName  : 'Group',
      width       : 170,
      valueGetter : (_value, row) => displayNameFor(catalog, 'group', row.groupInfo?.group),
    },
    {
      field       : 'actions',
      type        : 'actions',
      headerName  : 'Actions',
      width       : 100,
      headerClassName : 'actions-column-header',
      cellClassName   : 'actions-column',
      getActions  : (params) => {
        const row       = params.row as InsightRecord
        const isBusy    = busy.has(row.documentId)
        const publishOk = canPublish?.(row) ?? false

        const actions = [
          <GridActionsCellItem
            key="open"
            icon={(
              <Tooltip title="View insight">
                <VisibilityOutlinedIcon fontSize="small" />
              </Tooltip>
            )}
            label="View"
            onClick={() => onEdit(row)}
          />,
        ]

        if (onPublish) {
          /* Tooltip needs a non-disabled element to receive pointer events, so
             the icon is wrapped — otherwise the explanation for WHY publish is
             unavailable never appears, which is the only reason to show a
             disabled control rather than hiding it. */
          const reason = publishOk
            ? 'Publish'
            : `Cannot publish from ${statusLabels[row.verificationStatus] ?? row.verificationStatus}`

          actions.push(
            <GridActionsCellItem
              key="publish"
              icon={(
                <Tooltip title={reason}>
                  <span style={{ display : 'inline-flex' }}>
                    <PublishIcon fontSize="small" color={publishOk ? 'success' : 'disabled'} />
                  </span>
                </Tooltip>
              )}
              label="Publish"
              disabled={!publishOk || isBusy}
              onClick={() => onPublish(row)}
            />,
          )
        }

        return actions
      },
    },
  ], [catalog, statusLabels, busy, theme.palette.primary.main,
      onEdit, onPublish, canPublish, onChangeQaStatus, onToggleDisabled])

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    onPageChange(model.page + 1, model.pageSize)
  }

  // DataGridPro v8 models selection as `{ type, ids: Set }`, not the v7 array —
  // and the footer's row-count selector reads `.ids.size` unconditionally, so
  // handing it an array throws before the grid paints. Rebuild the object form
  // from the store's `string[]` on every render of a new selection.
  const rowSelectionModel = useMemo<GridRowSelectionModel>(
    () => ({ type : 'include', ids : new Set<GridRowId>(selectedIds) }),
    [selectedIds],
  )

  const handleSelectionChange = (model: GridRowSelectionModel) => {
    // `type: 'exclude'` is what the header select-all checkbox emits: it means
    // "everything except these". Expand it against the rows we hold — the store
    // and every bulk action downstream assume an explicit string[] of ids.
    const ids = model.type === 'exclude'
      ? rows.map((row) => row.documentId).filter((id) => !model.ids.has(id))
      : [...model.ids]
    onSelectionChange(ids.map(String))
  }

  return (
    <Box sx={{
      height       : 'calc(100vh - 300px)',
      minHeight    : 420,
      width        : '100%',
      borderRadius : 1,
      overflow     : 'hidden',
      bgcolor      : 'background.paper',
    }}>
      <DataGridPro
        rows={rows}
        columns={columns}
        loading={isLoading}
        getRowId={(row) => row.documentId}

        /* Both variants pinned deliberately. The grid's default for a load with
           no rows yet is `skeleton` — placeholder rows that FLOW in the scroll
           container rather than overlaying it (the sticky/zero-height styling is
           skipped for that variant alone). So a cold load painted a block of
           grey pseudo-rows, then replaced them with the real ones at a different
           offset. A progress indicator says the same thing without pretending to
           be data and without moving anything. */
        slotProps={{
          loadingOverlay : {
            // Rows already on screen — a bar under the headers, they stay put.
            variant       : 'linear-progress',
            // Nothing on screen yet — a spinner in the empty viewport.
            noRowsVariant : 'circular-progress',
          },
        }}

        /* Client mode on purpose — see the header comment. The whole pilot is
           already in memory, so server paging would be a round trip for data we
           hold. */
        pagination
        paginationMode="client"
        sortingMode="client"
        filterMode="client"
        paginationModel={{ page : page - 1, pageSize }}
        onPaginationModelChange={handlePaginationModelChange}
        pageSizeOptions={[10, 25, 50, 100]}

        checkboxSelection
        disableRowSelectionOnClick
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={handleSelectionChange}

        onRowDoubleClick={(params) => onEdit(params.row as InsightRecord)}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even-row' : 'odd-row'
        }

        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={setColumnVisibilityModel}

        density={density}
        onDensityChange={setDensity}

        initialState={{ pinnedColumns : { right : ['actions'] } }}

        sx={{
          border  : 'none',
          bgcolor : 'background.paper',
          // Tighter than the DataGrid default of 10px a side. With this many
          // columns the default gutters cost more horizontal room than the
          // content does, and the grid starts scrolling before it needs to.
          '& .MuiDataGrid-columnHeader' : { px : 1 },
          '& .MuiDataGrid-columnHeaders' : {
            bgcolor      : theme.palette.mode === 'dark' ? 'grey.800' : '#f8f9fa',
            borderBottom : `2px solid ${theme.palette.divider}`,
            '& .MuiDataGrid-columnHeaderTitle' : {
              fontWeight    : 600,
              fontSize      : '0.8125rem',
              color         : theme.palette.text.secondary,
              textTransform : 'uppercase',
              letterSpacing : '0.5px',
            },
          },
          '& .MuiDataGrid-row' : {
            transition : 'background-color 0.15s ease',
            '&.odd-row' : {
              bgcolor : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)',
            },
            '&:hover' : {
              bgcolor : theme.palette.mode === 'dark'
                ? 'rgba(25, 118, 210, 0.12)'
                : 'rgba(25, 118, 210, 0.06)',
            },
          },
          '& .MuiDataGrid-cell' : {
            borderBottom : `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.06)'}`,
            py           : 1,
            px           : 1,
            fontSize     : '0.875rem',
            display      : 'flex',
            alignItems   : 'center',
            '&:focus'        : { outline : 'none' },
            '&:focus-within' : { outline : 'none' },
          },
          '& .MuiDataGrid-footerContainer' : {
            borderTop : `2px solid ${theme.palette.divider}`,
            bgcolor   : theme.palette.mode === 'dark' ? 'grey.800' : '#f8f9fa',
          },
          '& .MuiDataGrid-columnSeparator' : { display : 'none' },
          '& .actions-column-header' : { borderLeft : `2px solid ${theme.palette.divider}` },
          '& .actions-column' : {
            borderLeft : `2px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
          },

          /* ⚠️ Do NOT put a height or minHeight on `.MuiDataGrid-overlayWrapper`.
             The grid gives that element `width: 0; height: 0` on purpose — its
             own comment reads "To stay above the content instead of shifting it
             down" — and `.MuiDataGrid-overlayWrapperInner` is then sized from
             the measured viewport. A `minHeight: 300` here (copied over from
             ConfigRegistryTable, which still carries it) inflated the wrapper
             back into the flow, so every row was pushed 300px down for the
             length of the fetch and snapped back up when the overlay unmounted.
             The empty state needs no help: the wrapper's height is calculated
             from the viewport, and this grid has a fixed height already. */
        }}
      />
    </Box>
  )
}

export default InsightsTable
